export default function About() {
  const team = [
    { name: "Mr. Chandeshwwar", role: "Founder", emoji: "👨‍💼", desc: "Serial entrepreneur. Built 3 fitness apps. 10K steps/day minimum." },
    { name: "Mr. Pavan", role: "Head of IT", emoji: "👨‍💻", desc: "Loves distributed systems and morning walks." },
    { name: "Mr. Rohit", role: "Head of Design", emoji: "🧑‍🎨", desc: "Created award-winning UX for 5 apps. Addicted to dark mode and competition." },
  ];

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", paddingTop: 80, overflowX: "hidden" }}>
      <style>{`
        .wb-about-hero {
          padding: 80px 48px 48px;
          text-align: center;
        }

        .wb-about-content {
          padding: 0 48px 80px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .wb-mission-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 60px;
          align-items: center;
          margin-bottom: 80px;
        }

        .wb-stats-mini-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .wb-team-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        /* Tablet (768px - 1024px) */
        @media (max-width: 1024px) {
          .wb-about-hero {
            padding: 60px 28px 40px;
          }
          .wb-about-content {
            padding: 0 28px 60px;
          }
          .wb-mission-grid {
            grid-template-columns: 1fr;
            gap: 40px;
            margin-bottom: 60px;
          }
          .wb-team-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
        }

        /* Mobile (< 640px) */
        @media (max-width: 640px) {
          .wb-about-hero {
            padding: 48px 16px 32px;
          }
          .wb-about-content {
            padding: 0 16px 50px;
          }
          .wb-mission-grid {
            grid-template-columns: 1fr;
            gap: 32px;
            margin-bottom: 48px;
          }
          .wb-stats-mini-grid {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          .wb-team-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>

      {/* Hero */}
      <section className="wb-about-hero">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "rgba(123,97,255,0.07)", border: "1px solid rgba(123,97,255,0.2)", padding: "6px 16px", borderRadius: 100, marginBottom: 20, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--neon3)", letterSpacing: 2, textTransform: "uppercase" }}>
          🏢 About Us
        </div>
        <h1 style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(28px, 5.5vw, 56px)", fontWeight: 900, marginBottom: 18, lineHeight: 1.1 }}>
          <span style={{ color: "var(--text)" }}>We Believe Fitness</span><br />
          <span style={{ background: "linear-gradient(90deg,var(--neon),var(--neon3))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", backgroundSize: "200%", animation: "gradShift 3s linear infinite" }}>Should Be a Sport</span>
        </h1>
        <p style={{ color: "var(--muted)", fontSize: "clamp(15px, 2vw, 17px)", maxWidth: 580, margin: "0 auto", lineHeight: 1.75 }}>
          WalkBattle was born from a simple frustration: why does walking feel like a chore? We gamified it. We made it competitive. Now 50,000 people walk more because they want to win.
        </p>
      </section>

      <section className="wb-about-content">
        {/* Mission & Stats */}
        <div className="wb-mission-grid">
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--neon)", letterSpacing: 3, textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 24, height: 1, background: "var(--neon)", display: "inline-block" }} /> Our Mission
            </div>
            <h2 style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(22px, 3.5vw, 36px)", fontWeight: 900, color: "var(--text)", marginBottom: 16, lineHeight: 1.15 }}>
              Make the World <span style={{ color: "var(--neon)" }}>Walk More</span>
            </h2>
            <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
              Physical inactivity is a global crisis. We fight it with competition, community, and real rewards. Our mission is to make walking the most compelling activity on your phone — not scrolling.
            </p>
            <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.8 }}>
              Every territory claimed, every battle won, every streak maintained — it all adds up to a healthier city, one step at a time.
            </p>
          </div>
          <div className="wb-stats-mini-grid">
            {[["2026", "Founded"], ["50K+", "Users"], ["200+", "Cities"], ["99.9%", "Uptime"]].map(([v, l]) => (
              <div key={l} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "24px 16px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(24px, 4vw, 30px)", fontWeight: 900, color: "var(--neon)", marginBottom: 4 }}>{v}</div>
                <div style={{ color: "var(--muted)", fontSize: 13, fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Team Section */}
        <div>
          <h2 style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(22px, 3.5vw, 36px)", fontWeight: 900, color: "var(--text)", marginBottom: 40, textAlign: "center" }}>
            Meet the <span style={{ color: "var(--neon2)" }}>Team</span>
          </h2>
          <div className="wb-team-grid">
            {team.map(({ name, role, emoji, desc }, i) => (
              <div key={name} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 24, textAlign: "center", animation: `fadeInUp 0.5s ${i * 0.12}s ease both`, transition: "all 0.3s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = "rgba(0,245,196,0.25)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.borderColor = "var(--border)"; }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>{emoji}</div>
                <h3 style={{ fontFamily: "'Orbitron', monospace", fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>{name}</h3>
                <div style={{ color: "var(--neon)", fontSize: 11, fontFamily: "monospace", marginBottom: 10, letterSpacing: 1 }}>{role}</div>
                <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

